import { useState, useEffect } from 'react'

// Gallery image interface (matches API response)
interface GalleryImage {
  id: string
  name: string
  title: string
  description: string
  category: string
  tags: string[]
  isActive: boolean
  downloadUrl: string
  thumbnailUrl: string
  libraryId: string
  eTag: string
  lastModified: string
}

function AdminArtGalleryManager() {
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([])
  const [editingImage, setEditingImage] = useState<Partial<GalleryImage> | null>(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterCategory, setFilterCategory] = useState<string>('all')
  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)

  const categories = ['Paintings', 'Sculptures', 'Photography', 'Mixed Media', 'Digital Art', 'Watercolor', 'Acrylic', 'Oil', 'Other']

  // Load gallery images from API (on-demand, no cache)
  const loadGalleryImages = async () => {
    setIsLoading(true)
    setError(null)
    try {
      console.log('Fetching gallery images from API (including inactive)...')
      const response = await fetch('/.netlify/functions/gallery-metadata?includeInactive=true')

      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`)
      }

      const data = await response.json()

      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch gallery images')
      }

      console.log('AdminArtGalleryManager: Loaded', data.images.length, 'gallery images from API')
      setGalleryImages(data.images)
    } catch (error) {
      console.error('AdminArtGalleryManager: Error loading gallery images:', error)
      setError(error instanceof Error ? error.message : 'Failed to load gallery images')
      setGalleryImages([])
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadGalleryImages()
  }, [])

  // Handle file selection for upload
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file type (including HEIC which might not have proper MIME type)
    const validExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp', '.heic', '.heif']
    const fileName = file.name.toLowerCase()
    const isValidExtension = validExtensions.some(ext => fileName.endsWith(ext))
    const isValidMimeType = file.type.startsWith('image/')

    if (!isValidExtension && !isValidMimeType) {
      alert('Please select a valid image file (jpg, png, gif, webp, svg, bmp, heic, heif)')
      return
    }

    setSelectedFile(file)

    // Create preview URL
    const reader = new FileReader()
    reader.onloadend = () => {
      setImagePreview(reader.result as string)
    }
    reader.readAsDataURL(file)
  }

  const handleSaveImage = async () => {
    if (!editingImage) return

    // Validate required fields
    if (!editingImage.title) {
      alert('Please provide a title')
      return
    }

    // For new images, require file upload
    if (!editingImage.id && !selectedFile) {
      alert('Please select an image file to upload')
      return
    }

    setIsSaving(true)
    setError(null)

    try {
      let imageId = editingImage.id
      let libraryId = editingImage.libraryId

      // If new image, upload file first
      if (!imageId && selectedFile) {
        console.log('Uploading new image to SharePoint...')
        setIsUploading(true)

        // Convert HEIC to JPEG if necessary
        let fileToUpload = selectedFile
        const fileExtension = selectedFile.name.split('.').pop()?.toLowerCase() || ''
        const isHEIC = fileExtension === 'heic' || fileExtension === 'heif'

        if (isHEIC) {
          console.log(`🔄 Converting HEIC image to JPEG: ${selectedFile.name}`)
          try {
            // Dynamically import heic2any
            const heic2any = (await import('heic2any')).default

            // Convert HEIC to JPEG
            const convertedBlob = await heic2any({
              blob: selectedFile,
              toType: 'image/jpeg',
              quality: 0.9
            })

            // Handle array of blobs
            const blobToUse = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob

            // Create new File object
            const originalName = selectedFile.name.replace(/\.(heic|heif)$/i, '')
            fileToUpload = new File([blobToUse], `${originalName}.jpg`, { type: 'image/jpeg' })

            console.log(`✅ Converted ${selectedFile.name} to JPEG (${(blobToUse.size / 1024).toFixed(2)} KB)`)
          } catch (conversionError: any) {
            throw new Error(`Failed to convert HEIC image: ${conversionError.message}`)
          }
        }

        // Upload via Netlify Function (server-side auth, no popups!)
        const formData = new FormData()
        formData.append('file', fileToUpload)

        const uploadResponse = await fetch('/.netlify/functions/upload-gallery-image', {
          method: 'POST',
          body: formData
        })

        if (!uploadResponse.ok) {
          const errorData = await uploadResponse.json()
          throw new Error(errorData.message || `Upload failed: ${uploadResponse.status}`)
        }

        const uploadResult = await uploadResponse.json()
        if (!uploadResult.success) {
          throw new Error(uploadResult.message || 'Upload failed')
        }

        imageId = uploadResult.driveItemId
        libraryId = uploadResult.libraryId
        console.log('✅ Image uploaded successfully:', imageId)
      }

      // Now update/set the metadata
      if (imageId && libraryId) {
        console.log('Updating image metadata:', imageId)

        const response = await fetch('/.netlify/functions/update-gallery-metadata', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            imageId: imageId,
            libraryId: libraryId,
            metadata: {
              Title: editingImage.title,
              ArtworkDescription: editingImage.description || '',
              Category: editingImage.category || 'gallery',
              Tags: editingImage.tags?.join(', ') || '',
              IsActive: editingImage.isActive !== false
            }
          })
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.message || `Update failed: ${response.status}`)
        }

        const result = await response.json()
        if (!result.success) {
          throw new Error(result.message || 'Update failed')
        }

        console.log('✅ Image metadata updated successfully')
      }

      // Reload images to reflect changes
      await loadGalleryImages()

      // Close form and reset state
      setEditingImage(null)
      setShowAddForm(false)
      setSelectedFile(null)
      setImagePreview(null)
    } catch (error) {
      console.error('Error saving image:', error)
      setError(error instanceof Error ? error.message : 'Failed to save image')
      alert('Error saving image. Please try again.')
    } finally {
      setIsSaving(false)
      setIsUploading(false)
    }
  }

  const handleDeleteImage = async (imageId: string, libraryId: string) => {
    if (!confirm('Are you sure you want to delete this image from SharePoint? This action cannot be undone.')) {
      return
    }

    setError(null)

    try {
      // Call Netlify Function to delete from SharePoint
      const response = await fetch('/.netlify/functions/delete-gallery-image', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          imageId: imageId,
          libraryId: libraryId
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || `Delete failed: ${response.status}`)
      }

      const result = await response.json()
      if (!result.success) {
        throw new Error(result.message || 'Delete failed')
      }

      console.log('✅ Image deleted successfully')

      // Reload images to reflect deletion
      await loadGalleryImages()
    } catch (error) {
      console.error('Error deleting image:', error)
      setError(error instanceof Error ? error.message : 'Failed to delete image')
      alert('Error deleting image. Please try again.')
    }
  }

  const handleToggleActive = async (imageId: string, libraryId: string, currentStatus: boolean) => {
    setError(null)

    try {
      // Call Netlify Function to update SharePoint
      const response = await fetch('/.netlify/functions/update-gallery-metadata', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          imageId: imageId,
          libraryId: libraryId,
          metadata: {
            IsActive: !currentStatus
          }
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || `Update failed: ${response.status}`)
      }

      const result = await response.json()
      if (!result.success) {
        throw new Error(result.message || 'Update failed')
      }

      console.log('✅ Image status toggled successfully')

      // Reload images to reflect change
      await loadGalleryImages()
    } catch (error) {
      console.error('Error toggling image status:', error)
      setError(error instanceof Error ? error.message : 'Failed to toggle image status')
      alert('Error updating image status. Please try again.')
    }
  }

  // Filter images
  const filteredImages = galleryImages.filter(img => {
    const matchesSearch = searchTerm === '' ||
      (img.title && img.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (img.description && img.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (img.tags && img.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())))

    const matchesCategory = filterCategory === 'all' || img.category === filterCategory

    return matchesSearch && matchesCategory
  })

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>Art Gallery Manager (SharePoint)</h2>
          <small className="text-muted">
            {galleryImages.length} images loaded from SharePoint | Data refreshes on every page load
          </small>
        </div>
        <div>
          <button
            className="btn btn-success me-2"
            onClick={() => {
              setEditingImage({
                title: '',
                description: '',
                category: 'gallery',
                tags: [],
                isActive: true
              })
              setShowAddForm(true)
              setSelectedFile(null)
              setImagePreview(null)
            }}
          >
            + Add New Image
          </button>
          <button
            className="btn btn-primary"
            onClick={loadGalleryImages}
            disabled={isLoading || isSaving}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                Loading...
              </>
            ) : (
              <>🔄 Refresh</>
            )}
          </button>
        </div>
      </div>

      {/* Info Alert - First time use */}
      {!error && galleryImages.length === 0 && !isLoading && (
        <div className="alert alert-info" role="alert">
          <strong>ℹ️ Getting Started:</strong> Click "🔄 Refresh" to load images from SharePoint, or click "+ Add New Image" to upload your first image.
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <strong>Error:</strong> {error}
          <button type="button" className="btn-close" onClick={() => setError(null)}></button>
        </div>
      )}

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Search:</label>
              <input
                type="text"
                className="form-control"
                placeholder="Search by title, description, or tags..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Filter by Category:</label>
              <select
                className="form-select"
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
              >
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Image Grid */}
      <div className="row g-4 mb-4">
        {filteredImages.length > 0 ? (
          filteredImages.map((image) => (
            <div key={image.id} className="col-lg-3 col-md-4 col-sm-6">
              <div className={`card h-100 ${!image.isActive ? 'border-danger' : ''}`}>
                <div
                  className="card-img-top bg-light d-flex align-items-center justify-content-center"
                  style={{
                    height: '200px',
                    overflow: 'hidden',
                    position: 'relative'
                  }}
                >
                  {image.downloadUrl ? (
                    <img
                      src={image.downloadUrl}
                      alt={image.title || 'Gallery image'}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain'
                      }}
                    />
                  ) : (
                    <div className="display-4 text-muted">🖼️</div>
                  )}
                  {!image.isActive && (
                    <div
                      className="position-absolute top-0 end-0 bg-danger text-white px-2 py-1"
                      style={{ fontSize: '0.75rem' }}
                    >
                      Inactive
                    </div>
                  )}
                </div>
                <div className="card-body">
                  <h6 className="card-title">{image.title || 'Untitled'}</h6>
                  {image.description && (
                    <p className="card-text small text-muted">
                      {image.description.substring(0, 100)}
                      {image.description.length > 100 && '...'}
                    </p>
                  )}
                  <div className="mb-2">
                    <span className="badge bg-secondary">{image.category || 'gallery'}</span>
                  </div>
                  {image.tags && image.tags.length > 0 && (
                    <div className="mb-2">
                      {image.tags.slice(0, 3).map((tag, idx) => (
                        <span key={idx} className="badge bg-light text-dark me-1">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="card-footer">
                  <div className="btn-group w-100" role="group">
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => {
                        setEditingImage(image)
                        setShowAddForm(true)
                      }}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-sm btn-outline-warning"
                      onClick={() => handleToggleActive(image.id, image.libraryId, image.isActive)}
                    >
                      {image.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDeleteImage(image.id, image.libraryId)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-12">
            <div className="alert alert-info text-center">
              <h5>No gallery images found</h5>
              <p className="mb-0">
                {searchTerm || filterCategory !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Click "Add New Image" to get started'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Edit/Add Modal */}
      {showAddForm && editingImage && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowAddForm(false)
              setEditingImage(null)
              setSelectedFile(null)
              setImagePreview(null)
            }
          }}
        >
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingImage.id === 'new' ? 'Add New Image' : 'Edit Image'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowAddForm(false)
                    setEditingImage(null)
                    setSelectedFile(null)
                    setImagePreview(null)
                  }}
                />
              </div>
              <div className="modal-body">
                {!editingImage.id && (
                  <div className="mb-3">
                    <label className="form-label">Upload Image: *</label>
                    <input
                      type="file"
                      className="form-control"
                      accept=".jpg,.jpeg,.png,.gif,.webp,.svg,.bmp,.heic,.heif,image/jpeg,image/png,image/gif,image/webp,image/svg+xml,image/bmp"
                      onChange={handleFileSelect}
                    />
                    <div className="form-text">
                      Select an image file from your computer to upload to the gallery
                    </div>
                    {imagePreview && (
                      <div className="mt-3 text-center">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain' }}
                        />
                        <div className="small text-muted mt-2">
                          {selectedFile?.name} ({(selectedFile!.size / 1024 / 1024).toFixed(2)} MB)
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {editingImage.id && editingImage.downloadUrl && (
                  <div className="mb-3 text-center">
                    <label className="form-label">Current Image:</label>
                    <div>
                      <img
                        src={editingImage.downloadUrl}
                        alt={editingImage.title}
                        style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain' }}
                      />
                    </div>
                    <div className="small text-muted mt-2">{editingImage.name}</div>
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label">Title: *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editingImage.title || ''}
                    onChange={(e) =>
                      setEditingImage({ ...editingImage, title: e.target.value })
                    }
                    placeholder="e.g., Sunset Over the Ocean"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Description:</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={editingImage.description || ''}
                    onChange={(e) =>
                      setEditingImage({ ...editingImage, description: e.target.value })
                    }
                    placeholder="Brief description of the artwork..."
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Category:</label>
                  <select
                    className="form-select"
                    value={editingImage.category || 'gallery'}
                    onChange={(e) =>
                      setEditingImage({ ...editingImage, category: e.target.value })
                    }
                  >
                    <option value="gallery">General Gallery</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Tags (comma-separated):</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editingImage.tags?.join(', ') || ''}
                    onChange={(e) =>
                      setEditingImage({
                        ...editingImage,
                        tags: e.target.value.split(',').map(t => t.trim()).filter(t => t)
                      })
                    }
                    placeholder="e.g., ocean, sunset, coastal"
                  />
                </div>

                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={editingImage.isActive !== false}
                    onChange={(e) =>
                      setEditingImage({ ...editingImage, isActive: e.target.checked })
                    }
                    id="activeCheck"
                  />
                  <label className="form-check-label" htmlFor="activeCheck">
                    Active (visible on public gallery)
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddForm(false)
                    setEditingImage(null)
                  }}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveImage}
                  disabled={!editingImage.title || (!editingImage.id && !selectedFile) || isSaving}
                >
                  {isSaving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      {isUploading ? 'Uploading...' : 'Saving...'}
                    </>
                  ) : (
                    'Save Image'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminArtGalleryManager
